import { useState, useEffect, useCallback } from "react";
import apiClient from "../api/client";

/**
 * Custom hook for fetching and managing the materials list.
 *
 * Calls apiClient.listMaterials() on mount and exposes a refresh function.
 *
 * @returns {{ materials: Array, loading: boolean, error: string|null, fetchMaterials: Function }}
 */
export function useMaterials() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.listMaterials();
      setMaterials(data.materials || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete failures are thrown to the caller, which shows them in its own
  // dialog; they are not list-loading errors.
  const deleteMaterial = useCallback(async (id) => {
    await apiClient.deleteMaterial(id);
    setMaterials((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const clearAllMaterials = useCallback(async () => {
    await apiClient.clearAllMaterials();
    setMaterials([]);
  }, []);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  return { materials, loading, error, fetchMaterials, deleteMaterial, clearAllMaterials };
}

export default useMaterials;
